import * as api from './api';
import type { Page } from './api';
import { openTextPrompt } from './modal';
import { iconSvg } from './icons';
import { applyCssTheme, setupDomThemeToggle } from './theme';

// ─── Pages list view (DOM) ───────────────────────────────────────────────────
// Rendered into #pages-view when the route is "/". Plain HTML — no PixiJS.

const navigate = (path: string): void => {
  window.location.assign(path);
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function cardHtml(page: Page): string {
  const count = page.node_count === 1 ? '1 node' : `${page.node_count} nodes`;
  return `
    <article class="page-card" data-id="${page.id}" role="button" tabindex="0">
      <div class="page-card-actions">
        <button class="page-card-btn" data-action="rename" data-id="${page.id}"
                type="button" aria-label="Rename page">${iconSvg('edit', 15)}</button>
        <button class="page-card-btn" data-action="delete" data-id="${page.id}"
                type="button" aria-label="Delete page">${iconSvg('trash', 15)}</button>
      </div>
      <h2 class="page-card-title">${escapeHtml(page.title)}</h2>
      <p class="page-card-meta">${count} · ${formatDate(page.created_at)}</p>
    </article>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function renderPagesView(): Promise<void> {
  applyCssTheme();
  const root = document.getElementById('pages-view') as HTMLElement;

  const draw = (pages: Page[]): void => {
    const cards = pages.map(cardHtml).join('');
    root.innerHTML = `
      <div class="pages-shell">
        <header class="pages-header">
          <h1 class="app-logo" role="button" tabindex="0">Dictator</h1>
          <div class="pages-header-actions">
            <a class="pages-api-link" href="/api">API</a>
            <button id="theme-toggle-dom" class="theme-toggle-btn" type="button"></button>
            <button id="new-page-btn" type="button">${iconSvg('plus', 16)}<span>New page</span></button>
          </div>
        </header>
        <div class="pages-grid">
          ${cards}
          ${pages.length === 0 ? '<p class="pages-empty">No pages yet. Create your first one.</p>' : ''}
        </div>
      </div>`;
    wire(pages);
  };

  const wire = (_pages: Page[]): void => {
    (document.getElementById('new-page-btn') as HTMLButtonElement)
      .addEventListener('click', onCreate);

    setupDomThemeToggle(document.getElementById('theme-toggle-dom') as HTMLButtonElement);

    const logo = root.querySelector<HTMLElement>('.app-logo') as HTMLElement;
    logo.addEventListener('click', () => navigate('/'));
    logo.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); }
    });

    root.querySelectorAll<HTMLElement>('.page-card').forEach(card => {
      const id = card.dataset.id as string;
      card.addEventListener('click', e => {
        // Ignore clicks that originated on an action button.
        if ((e.target as HTMLElement).closest('.page-card-btn')) return;
        navigate(`/page/${id}`);
      });
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/page/${id}`);
        }
      });
    });

    root.querySelectorAll<HTMLButtonElement>('.page-card-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const id     = btn.dataset.id as string;
        const action = btn.dataset.action;
        if (action === 'rename') onRename(id);
        else if (action === 'delete') onDelete(id);
      });
    });
  };

  const onCreate = async (): Promise<void> => {
    const title = await openTextPrompt('New page');
    if (!title) return;
    try {
      const page = await api.createPage(title);
      navigate(`/page/${page.id}`);
    } catch (err) {
      console.warn('[pages] createPage failed', err);
    }
  };

  const onRename = async (id: string): Promise<void> => {
    const title = await openTextPrompt('Rename page');
    if (!title) return;
    try {
      await api.renamePage(id, title);
      draw(await api.fetchPages());
    } catch (err) {
      console.warn('[pages] renamePage failed', err);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Delete this page and all its nodes?')) return;
    try {
      await api.deletePage(id);
      draw(await api.fetchPages());
    } catch (err) {
      console.warn('[pages] deletePage failed', err);
    }
  };

  try {
    draw(await api.fetchPages());
  } catch (err) {
    console.warn('[pages] fetchPages failed', err);
    root.innerHTML = '<p class="pages-empty">Could not load pages. Is the backend running?</p>';
  }
}
